import type { SxProps, Theme } from "@mui/material";
import { keepPreviousData } from "@tanstack/react-query";
import { DateTime, Interval } from "luxon";
import type { InHouseBarDataGroup } from "../../components/UIToolkit/Graphs/BarChart/types";
import type { InHouseLineDataGroup } from "../../components/UIToolkit/Graphs/LineChart/types";
import { getWeekStart } from "../../utils/dateUtils";
import {
	type DisciplinaryReportResponse,
	DisciplinaryViolationType,
	type ManagementCenterDateToValueAggregation,
	type ManagementCenterStringToDatesAndValueAggregation,
} from "../../utils/models";

/**
 * Selected date range used to clamp the edge buckets of weekly/monthly groupings.
 * A week bucket that straddles the range edge would otherwise surface a date
 * outside the user's selection (e.g. a week starting before the chosen start),
 * so chart points and table labels are clamped to [start, end].
 */
export type ClampRange = { start: DateTime; end: DateTime };

/** Clamp a DateTime into [range.start, range.end]; no-op when range is omitted. */
export const clampDateToRange = (dt: DateTime, range?: ClampRange): DateTime => {
	if (!range) return dt;
	if (dt < range.start) return range.start;
	if (dt > range.end) return range.end;
	return dt;
};

/** Build the selected-range clamp window (dept tz, day-aligned) from the picker dates. `end` defaults to now. */
export const buildClampRange = (start: DateTime, end: DateTime | undefined, timezone: string): ClampRange => ({
	start: start.setZone(timezone).startOf("day"),
	end: (end ?? DateTime.now()).setZone(timezone).startOf("day"),
});

/**
 * Today's data is incomplete (shifts in progress, late actuals, unfinalized census), so the
 * Management Center treats the day this many days before "now" as the latest fully-available day
 * (INH-3519). Single source of truth — both `ManagementCenterHeader`'s range and the
 * `useManagementCenterFilters` default fallback derive from `getLatestAvailableDate`, so they can't
 * drift a day apart (which previously fired two staff-mix/cost queries with different start dates).
 *
 * Raised 1 -> 2: yesterday is not settled either. Late punches, corrections and the overnight
 * rollups all land after the fact, so a range ending yesterday showed figures that moved the next
 * morning. The latest day the Management Center will read is now the day before yesterday.
 */
export const MC_LATEST_AVAILABLE_DAY_OFFSET = 2;

/** The latest fully-available day in the department timezone (today − MC_LATEST_AVAILABLE_DAY_OFFSET). */
export const getLatestAvailableDate = (timezone: string): DateTime =>
	DateTime.now().setZone(timezone).minus({ days: MC_LATEST_AVAILABLE_DAY_OFFSET }).startOf("day");

/** Background color used on ManagementCenter page-level containers. */
export const MC_PAGE_BG = "#E7E9EF4D";

/** Background color used on expanded detail rows in MC tables. */
export const MC_EXPANDED_ROW_BG = "#F5F6FA";

/** Shared DataGrid sx overrides used across ManagementCenter tables. */
export const MC_GRID_SX = {
	border: "none",
	"& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
		outline: "none",
	},
	"& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within": {
		outline: "none",
	},
	"& .MuiDataGrid-columnSeparator": {
		display: "none",
	},
	"& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitleContainer": {
		flexDirection: "row",
	},
} as const satisfies SxProps<Theme>;

/** Shared TanStack Query options for ManagementCenter data hooks. */
export const MC_QUERY_DEFAULTS = {
	refetchOnWindowFocus: true,
	refetchOnMount: "always" as const,
	staleTime: 5 * 60 * 1000,
	placeholderData: keepPreviousData,
};

export const fetchedDataToBarDataGroups = ({
	nameToColor,
	nameTransformer,
	data,
	timezone,
	clampRange,
}: {
	data: ManagementCenterStringToDatesAndValueAggregation;
	timezone: string;
	nameToColor?: (name: string) => string | undefined;
	nameTransformer?: (name: string) => string;
	clampRange?: ClampRange;
}): InHouseBarDataGroup[][] => {
	if (!data) return [];
	const groups: InHouseBarDataGroup[][] = [[]];
	Object.entries(data).forEach(([nameKey, innerRecord]) => {
		const group: InHouseBarDataGroup = {
			name: nameTransformer ? nameTransformer(nameKey) : nameKey.trim().replaceAll("\n", "_"),
			data: Object.entries(innerRecord).map(([dateString, count]) => ({
				dateOrLabel: chartDateForKey(dateString, timezone, clampRange),
				value: count,
			})),
			color: nameToColor ? nameToColor(nameKey) : undefined,
		};
		groups[0].push(group);
	});
	return groups;
};

/** Converts a date-to-value map into a chart data group (works for both line and area charts). */
export const fetchedDataToChartDataGroup = ({
	color,
	data,
	name,
	timezone,
	strokeDasharray,
	clampRange,
}: {
	data: ManagementCenterDateToValueAggregation;
	name: string;
	color?: string;
	timezone: string;
	strokeDasharray?: string;
	clampRange?: ClampRange;
}): InHouseLineDataGroup => ({
	name,
	data: Object.entries(data).map(([dateString, value]) => ({
		date: chartDateForKey(dateString, timezone, clampRange),
		value: Number.parseFloat(value.toFixed(2)),
	})),
	color,
	strokeDasharray,
});

export const costToKDollars = (cost: number | undefined) => {
	if (cost === undefined) return undefined;
	return `$${(cost / 1000).toFixed(1)}K`;
};

const censusPadding = 3;
export const getCensusYDomain = (censusValues?: ManagementCenterDateToValueAggregation) => {
	if (!censusValues) {
		return undefined;
	}
	const maxCensus = Math.max(...Object.values(censusValues));
	const minCensus = Math.min(...Object.values(censusValues));

	if (maxCensus === undefined || minCensus === undefined) {
		return undefined;
	}
	return { max: Number.parseFloat((maxCensus * 1.1).toFixed(0)), min: Math.max(0, minCensus - censusPadding) };
};

const DISCIPLINARY_VIOLATION_LABELS = {
	[DisciplinaryViolationType.NCNS]: "No Call No Show",
	[DisciplinaryViolationType.CallIn]: "Call In",
	[DisciplinaryViolationType.LateArrival]: "Late Arrival",
	[DisciplinaryViolationType.HomeEarly]: "Home Early",
	[DisciplinaryViolationType.ShiftDelta]: "Shift Delta",
	[DisciplinaryViolationType.SkippedLunch]: "Skipped Lunch",
	// In and Out are one series here — the chart tracks violation types, not which half was absent.
	[DisciplinaryViolationType.MissingPunch]: "Missing Punch",
} as const satisfies Record<DisciplinaryViolationType, string>;

const DISCIPLINARY_COLORS = {
	"No Call No Show": "#CE93D8",
	"Call In": "var(--inhouse-primary-light)",
	"Late Arrival": "#FFB5A7",
	"Home Early": "#FFC75F",
	"Shift Delta": "#E57373",
	"Skipped Lunch": "#4DB6AC",
	"Missing Punch": "#4FC3F7",
} as const satisfies Record<(typeof DISCIPLINARY_VIOLATION_LABELS)[keyof typeof DISCIPLINARY_VIOLATION_LABELS], string>;

export const disciplinaryDataToLineDataGroups = (data: DisciplinaryReportResponse, timezone: string): InHouseLineDataGroup[] => {
	const countsByCategory: Record<string, Record<string, number>> = {};

	const increment = (category: string, dateISO: string) => {
		if (!countsByCategory[category]) countsByCategory[category] = {};
		countsByCategory[category][dateISO] = (countsByCategory[category][dateISO] || 0) + 1;
	};

	for (const nurseData of Object.values(data.disciplinaryData)) {
		for (const violation of nurseData.violations) {
			const label = DISCIPLINARY_VIOLATION_LABELS[violation.violationType] ?? violation.violationType;
			increment(label, violation.date);
		}
	}

	const allDates = new Set<string>();
	for (const dateMap of Object.values(countsByCategory)) {
		for (const dateISO of Object.keys(dateMap)) {
			allDates.add(dateISO);
		}
	}

	for (const dateMap of Object.values(countsByCategory)) {
		for (const dateISO of allDates) {
			if (!(dateISO in dateMap)) {
				dateMap[dateISO] = 0;
			}
		}
	}

	return Object.entries(countsByCategory).map(([category, dateMap]) =>
		fetchedDataToChartDataGroup({
			data: dateMap,
			name: category,
			timezone,
			color: DISCIPLINARY_COLORS[category as keyof typeof DISCIPLINARY_COLORS],
		}),
	);
};

/** Sorts rows by a DataGrid sort model. Supports date sorting (by ISO) and numeric field sorting.
 *  Pass customComparators for fields that need special sort logic (e.g. percentage-based difference). */
export const sortRowsByModel = <T extends { date: string }>(
	rows: T[],
	sortModel: readonly { field: string; sort: "asc" | "desc" | null | undefined }[],
	customComparators?: Record<string, (a: T, b: T) => number>,
): T[] => {
	if (sortModel.length === 0 || !sortModel[0].sort) return rows;
	const { field, sort } = sortModel[0];
	const sorted = [...rows];
	sorted.sort((a, b) => {
		let cmp = 0;
		if (customComparators?.[field]) {
			cmp = customComparators[field](a, b);
		} else if (field === "dateDisplay") {
			// Zone-agnostic sort key: ordering only. Parsing both keys in the same fixed zone (UTC)
			// preserves chronological order regardless of the department tz, and this generic helper
			// has no tz in scope. Display dates are formatted separately via formatDateKey (real tz).
			cmp = parseDateKey(a.date, "utc").toMillis() - parseDateKey(b.date, "utc").toMillis();
		} else {
			cmp = (a[field as keyof T] as number) - (b[field as keyof T] as number);
		}
		return sort === "asc" ? cmp : -cmp;
	});
	return sorted;
};

/** Row type with detail-row metadata, used for expandable DataGrid rows. */
export type MCTableAugmentedRow<T> = T & { _isDetail: boolean; _parentDate: string };

/** Builds an augmented row array that interleaves detail placeholder rows for expanded groups. */
export const buildAugmentedRows = <T extends { date: string }>(
	sortedRows: T[],
	expandedGroups: Set<string>,
	emptyRow: Omit<T, "date">,
): MCTableAugmentedRow<T>[] => {
	const result: MCTableAugmentedRow<T>[] = [];
	for (const row of sortedRows) {
		result.push({ ...row, _isDetail: false, _parentDate: "" });
		if (expandedGroups.has(row.date)) {
			result.push({
				...emptyRow,
				date: `${row.date}-detail`,
				_isDetail: true,
				_parentDate: row.date,
			} as MCTableAugmentedRow<T>);
		}
	}
	return result;
};

/**
 * Suffix the backend appends to weekly bucket keys. Weekly keys are the
 * week-start ISO date (already aligned to the department's week_start_day) plus
 * this marker, e.g. "2025-06-01/W". Keeps them distinguishable from daily keys
 * while staying directly parseable to the week-start date (no ±1 day shifting).
 */
export const WEEK_KEY_SUFFIX = "/W";

/** Checks whether a date key represents a grouped week (e.g. "2025-06-01/W"). */
export const isWeekKey = (key: string) => key.endsWith(WEEK_KEY_SUFFIX);

/** Checks whether a date key represents a grouped month (e.g. "2025-03"), not a full date. */
export const isMonthKey = (key: string) => /^\d{4}-\d{2}$/.test(key);

/**
 * Parse any management-center date key (daily ISO date, weekly "<date>/W", or
 * monthly "YYYY-MM") into the DateTime at the start of the period it represents.
 * Weekly keys resolve to their week-start date with no arithmetic, so the chart
 * and table render the exact same anchor the backend bucketed against.
 */
export const parseDateKey = (key: string, timezone: string): DateTime =>
	DateTime.fromISO(isWeekKey(key) ? key.slice(0, -WEEK_KEY_SUFFIX.length) : key)
		.setZone(timezone)
		.startOf("day");

/**
 * Parse a key to its chart x-date, clamping ONLY grouped (week/month) bucket anchors to the
 * selected range. A grouped bucket's anchor is a label artifact (the bucket's data is in-range) and
 * there is at most one edge bucket, so clamping it inward is safe. Daily keys are exact days and are
 * never clamped — clamping them would collapse any out-of-range day onto the boundary and overwrite
 * the real boundary day (INH: cost-variance returns a day past endDate that was landing on it).
 */
export const chartDateForKey = (key: string, timezone: string, clampRange?: ClampRange): DateTime => {
	const dt = parseDateKey(key, timezone);
	return isWeekKey(key) || isMonthKey(key) ? clampDateToRange(dt, clampRange) : dt;
};

type GroupingMode = "daily" | "weekly" | "monthly";

/** Detects the grouping mode from raw BE response keys. */
export const detectGroupingMode = (data: ManagementCenterStringToDatesAndValueAggregation): GroupingMode => {
	const firstKey = Object.values(data).flatMap((r) => Object.keys(r))[0] ?? "";
	if (!firstKey) return "daily";
	if (isWeekKey(firstKey)) return "weekly";
	if (isMonthKey(firstKey)) return "monthly";
	return "daily";
};

/** Generates a week key (week-start ISO date + suffix) aligned to the department's week start, matching the BE's `_get_group_key`. */
const toWeekKey = (dt: DateTime, weekStartDay: number): string => `${getWeekStart(dt, weekStartDay).toISODate() ?? ""}${WEEK_KEY_SUFFIX}`;

/** Generates all period keys for the given date range and grouping mode, as DateTime values parsed the same way the chart uses. */
export const generateAllPeriodDateTimes = ({
	dateFrom,
	dateTo,
	mode,
	timezone,
	weekStartDay,
}: {
	dateFrom: DateTime;
	dateTo: DateTime;
	mode: GroupingMode;
	timezone: string;
	weekStartDay: number;
}): { key: string; dateTime: DateTime }[] => {
	const start = dateFrom.setZone(timezone).startOf("day");
	const end = dateTo.setZone(timezone).startOf("day");

	if (mode === "daily") {
		return Interval.fromDateTimes(start, end.plus({ days: 1 }))
			.splitBy({ days: 1 })
			.flatMap((i) => {
				if (!i.start) return [];
				const d = i.start.startOf("day");
				return [{ key: d.toISODate() ?? "", dateTime: d }];
			});
	}

	if (mode === "weekly") {
		const seen = new Set<string>();
		return Interval.fromDateTimes(start, end.plus({ days: 1 }))
			.splitBy({ weeks: 1 })
			.flatMap((i) => {
				if (!i.start) return [];
				const key = toWeekKey(i.start, weekStartDay);
				if (seen.has(key)) return [];
				seen.add(key);
				return [{ key, dateTime: parseDateKey(key, timezone) }];
			});
	}

	// monthly
	return Interval.fromDateTimes(start.startOf("month"), end.endOf("month"))
		.splitBy({ months: 1 })
		.flatMap((i) => {
			if (!i.start) return [];
			const key = i.start.toFormat("yyyy-MM");
			const dt = DateTime.fromISO(key).setZone(timezone).startOf("day");
			return [{ key, dateTime: dt }];
		});
};

/** Convert a week key (e.g. "2025-06-01/W") to its start/end dates (week-start aligned, 7-day span). */
export const weekKeyToDateRange = (weekKey: string): { start: string; end: string } => {
	// Parse the week-start as a plain calendar date via the `zone` option (NOT parse-then-setZone):
	// the output is date strings for an API query, so the day must round-trip exactly regardless of
	// the browser's local zone. `fromISO(str).setZone("utc")` would shift the date back a day for
	// browsers east of UTC.
	const weekStart = DateTime.fromISO(isWeekKey(weekKey) ? weekKey.slice(0, -WEEK_KEY_SUFFIX.length) : weekKey, { zone: "utc" });
	const weekEnd = weekStart.plus({ days: 6 });
	return { start: weekStart.toFormat("yyyy-MM-dd"), end: weekEnd.toFormat("yyyy-MM-dd") };
};

/**
 * Formats a date key for display. Handles both daily (ISO date) and weekly ("<date>/W") keys.
 * When `clampRange` is provided, a weekly bucket's displayed start/end is clamped to the selected
 * range so an edge week never shows a date outside what the user selected.
 */
export const formatDateKey = (dateStr: string, timezone: string, clampRange?: ClampRange): string => {
	if (isWeekKey(dateStr)) {
		const weekStart = clampDateToRange(parseDateKey(dateStr, timezone), clampRange);
		const weekEnd = clampDateToRange(parseDateKey(dateStr, timezone).plus({ days: 6 }), clampRange);
		if (weekStart.month === weekEnd.month) {
			return `${weekStart.toFormat("MMM d")} - ${weekEnd.toFormat("d, yyyy")}`;
		}
		return `${weekStart.toFormat("MMM d")} - ${weekEnd.toFormat("MMM d, yyyy")}`;
	}
	return DateTime.fromISO(dateStr).setZone(timezone).toFormat("EEE, d MMM yyyy");
};

/** Parses a DateTime from a URL search param, falling back to a default value. */
// Overloads so that callers can choose whether to provide a fallback (and get a guaranteed DateTime type) or not (and get undefined if parsing fails)
export function parseDateFromSearchParams(searchParams: URLSearchParams, key: string, timezone: string, fallback: DateTime): DateTime;

export function parseDateFromSearchParams(searchParams: URLSearchParams, key: string, timezone: string, fallback?: DateTime): DateTime | undefined;

export function parseDateFromSearchParams(searchParams: URLSearchParams, key: string, timezone: string, fallback?: DateTime): DateTime | undefined {
	const param = searchParams.get(key);
	if (param) {
		const parsed = DateTime.fromISO(param, { zone: timezone });
		if (parsed.isValid) return parsed;
	}
	return fallback;
}
